import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@repo/ui";
import { Protected, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useEffect, useMemo, useState } from "react";

export default function CompaniesPage() {
  return (
    <Protected redirectTo="/login">
      <Content />
    </Protected>
  );
}

function Content() {
  const authFetch = useAuthenticatedFetch();
  const [q, setQ] = useState("");
  type Company = { id: number; name: string; website?: string; notes?: string };
  const [data, setData] = useState<Company[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);

  const load = async () => {
    const res = await authFetch(`/api/companies?q=${encodeURIComponent(q)}`);
    setData((await res.json()) as Company[]);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload: { name: string; website?: string; notes?: string } = {
      name: String(form.get("name") || ""),
      website: form.get("website") ? String(form.get("website")) : undefined,
      notes: form.get("notes") ? String(form.get("notes")) : undefined,
    };
    if (editing?.id) {
      await authFetch(`/api/companies/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    } else {
      await authFetch(`/api/companies`, { method: "POST", body: JSON.stringify(payload) });
    }
    setOpen(false);
    setEditing(null);
    await load();
  };

  const startEdit = (row: Company) => {
    setEditing(row);
    setOpen(true);
  };

  const remove = async (id: number) => {
    if (!confirm("Delete company?")) return;
    await authFetch(`/api/companies/${id}`, { method: "DELETE" });
    await load();
  };

  const rows = useMemo<Company[]>(() => data, [data]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search companies"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(null)}>New Company</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Company" : "New Company"}</DialogTitle>
            </DialogHeader>
            <form className="space-y-3" onSubmit={onSubmit}>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" name="name" defaultValue={editing?.name || ""} required />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  defaultValue={editing?.website || ""}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" name="notes" defaultValue={editing?.notes || ""} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Website</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>
                  {r.website ? (
                    <a
                      href={r.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline"
                    >
                      {r.website}
                    </a>
                  ) : null}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(r)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
